import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSettingsStore } from "../../../../stores/appSettingsStore";

const LANGUAGES: { code: string; labelKey: string; ariaKey: string }[] = [
  { code: "en", labelKey: "landing.language.en", ariaKey: "landing.language.selectEn" },
  { code: "nl", labelKey: "landing.language.nl", ariaKey: "landing.language.selectNl" },
];

interface LanguageSwitchProps {
  /** Smaller type, matching the mobile nav links. */
  mobile?: boolean;
}

/**
 * Current language + chevron, opening a small dropdown with the available
 * languages. Styled as a plain header link (the landing page forces light
 * mode, so no `dark:` variants).
 */
const LanguageSwitch: React.FC<LanguageSwitchProps> = ({ mobile = false }) => {
  const { t } = useTranslation();
  const { language, setLanguage } = useAppSettingsStore();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCode = language?.startsWith("nl") ? "nl" : "en";

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    const onMouseDown = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return (): void => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t("landing.language.label")}
        className={`flex items-center gap-1 text-white ${mobile ? "text-[16px]" : "text-[18px]"} leading-6 font-medium transition-opacity duration-200 hover:opacity-75 focus-visible:outline-none focus-visible:opacity-75`}
      >
        {t(`landing.language.${activeCode}`)}
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t("landing.language.label")}
          className="absolute left-0 z-40 mt-2 min-w-[72px] rounded-xl bg-white p-1 shadow-[0px_8px_24px_rgba(1,4,11,0.25)]"
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === activeCode;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                aria-label={t(lang.ariaKey)}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`block w-full rounded-lg px-3 py-1.5 text-left text-[15px] leading-5 font-medium transition-colors duration-150 ${
                  isActive ? "bg-accent/10 text-primary" : "text-secondary hover:bg-accent/5"
                }`}
              >
                {t(lang.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitch;
